/**
 * Splash Screen Module with GSAP
 *
 * This module provides a comprehensive splash screen system for web applications with:
 * - Enter animations: Full-screen splash on page load with horizontal split exit
 * - Exit animations: Smooth transitions when navigating away from pages
 * - Performance optimized: Uses element cloning and clip-path for smooth animations
 * - Zero-flash loading: Coordinates with CSS for instant rendering
 *
 * @example
 * // Initialize splash screen (auto-starts on page load)
 * const splash = new SplashScreen();
 *
 * // Manually trigger exit animation
 * splash.showExit();
 */

import { gsap } from "gsap";
import { explodeLogo } from "./exploding-logo";

// Configuration constants
const SPLASH_CONFIG = {
  // Timing settings
  START_SPLASH_DURATION: 3000, // 3 seconds for homepage splash
  PAGE_SPLASH_DURATION: 500, // 1 second for other pages
  ANIMATION_DURATION: 0.8, // Duration for split animation

  // Visual settings
  Z_INDEX: 9999, // High z-index for overlay

  // Animation positions
  OFF_SCREEN_TOP: "-100%", // Position for top half when hidden
  OFF_SCREEN_BOTTOM: "100%", // Position for bottom half when hidden

  // Clip-path patterns for horizontal split
  // Top half: Shows top 50% of element (0% to 50% vertically)
  TOP_HALF_CLIP: "polygon(0% 0%, 100% 0%, 100% 50%, 0% 50%)",
  // Bottom half: Shows bottom 50% of element (50% to 100% vertically)
  BOTTOM_HALF_CLIP: "polygon(0% 50%, 100% 50%, 100% 100%, 0% 100%)",

  // CSS classes
  HALF_BASE_CLASS: "fixed inset-0 bg-white-80 pointer-events-none",

  // Animation easing
  EASING: "power2.inOut",
};

type SplashHalves = {
  topHalf: HTMLElement;
  bottomHalf: HTMLElement;
};

type AnimatedHalvesOptions = {
  topId?: string;
  bottomId?: string;
  copyContent?: boolean;
  baseClass?: string;
};

type Callback = () => void;

// Extend the Window interface for custom properties
declare global {
  interface Window {
    SplashScreen: typeof SplashScreen;
    splashScreenInstance: SplashScreen;
    showExitSplash: (callback?: Callback) => void;
    navigateWithSplash: (url: string) => void;
  }
}

class SplashScreen {
  splashElement: HTMLElement | null;
  isAnimating: boolean;
  timeline: GSAPTimeline | null;
  originalOverflow?: string;
  originalPaddingRight?: string;

  constructor(options: Record<string, unknown> = {}) {
    this.splashElement = null;
    this.isAnimating = false;
    this.timeline = null;

    this.init();
  }

  private init(): void {
    if (this.isDebugNoSplash()) {
      console.info("SplashScreen disabled via hash (#no-splash)");
      this.cleanupExitSplash();
      this.cleanupEnterSplash();
      return;
    }

    this.cleanupExitSplash();

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        this.show();
        this.setupHistoryHandlers();
      });
    } else {
      this.show();
      this.setupHistoryHandlers();
    }
  }

  cleanupExitSplash(): void {
    const exitElements = document.querySelectorAll<HTMLElement>(
      "#exit-splash-top-half, #exit-splash-bottom-half, #page-splash-exit:not(.hidden)"
    );
    exitElements.forEach((el) => {
      if (el.id === "page-splash-exit") {
        el.classList.add("hidden");
      } else {
        el.remove();
      }
    });

    this.restoreScrollWithoutJump();
  }
  cleanupEnterSplash(): void {
    const enterElements = document.querySelectorAll<HTMLElement>(
      "#enter-splash-top-half, #enter-splash-bottom-half, #page-splash-enter:not(.hidden), #start-splash-enter"
    );
    enterElements.forEach((el) => {
      if (el.id === "page-splash-enter") {
        el.classList.add("hidden");
      }
      if (el.id === "start-splash-enter") {
        el.classList.add("hidden");
      } else {
        el.remove();
      }
    });

    this.restoreScrollWithoutJump();
  }

  private setupHistoryHandlers(): void {
    window.addEventListener("beforeunload", (event: BeforeUnloadEvent) => {
      const exitSplash = document.getElementById(
        "page-splash-exit"
      ) as HTMLElement | null;

      if (exitSplash && !this.isAnimating) {
        exitSplash.classList.remove("hidden");
        this.preventScrollWithoutJump();

        gsap.to(exitSplash, {
          duration: 0.2,
          scale: 1.02,
          ease: "power2.out",
        });
      }
    });

    window.navigateWithSplash = (url: string) => {
      this.showExit(() => {
        window.location.href = url;
      });
    };

    this.setupLinkHandlers();
  }

  private setupLinkHandlers(): void {
    // console.log("Setting up link handlers...");

    document.addEventListener("click", (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest("a[href]") as HTMLAnchorElement | null;

      if (link && !link.hasAttribute("data-no-splash") && !link.classList.contains("no-splash")) {
        const href = link.getAttribute("href");
        if (
          href &&
          !href.startsWith("http") &&
          !href.startsWith("mailto:") &&
          !href.startsWith("tel:") &&
          !href.startsWith("#")
        ) {
          // Parse the link to get the pathname without hash/query
          const linkUrl = new URL(href, window.location.origin);
          const currentUrl = new URL(window.location.href);

          // Only trigger splash if navigating to a different page (not same page with hash/query)
          if (linkUrl.pathname !== currentUrl.pathname) {
            event.preventDefault();
            event.stopPropagation();

            const exitSplash = document.getElementById(
              "page-splash-exit"
            ) as HTMLElement | null;
            if (exitSplash) {
              exitSplash.style.setProperty("display", "block", "important");
              exitSplash.style.setProperty("opacity", "1", "important");
              exitSplash.style.setProperty("visibility", "visible", "important");
              exitSplash.classList.remove("hidden");
              this.preventScrollWithoutJump();
            }

            requestAnimationFrame(() => {
              this.navigateWithSplash(href);
            });
          }
        }
      }
    });
  }

  private createAnimatedHalves(
    options: AnimatedHalvesOptions = {}
  ): SplashHalves {
    const {
      topId = "splash-top-half",
      bottomId = "splash-bottom-half",
      copyContent = false,
      baseClass = SPLASH_CONFIG.HALF_BASE_CLASS,
    } = options;

    const topHalf = document.createElement("div");
    const bottomHalf = document.createElement("div");

    topHalf.id = topId;
    bottomHalf.id = bottomId;

    topHalf.className = `${baseClass} z-[${SPLASH_CONFIG.Z_INDEX}]`;
    bottomHalf.className = `${baseClass} z-[${SPLASH_CONFIG.Z_INDEX}]`;

    if (copyContent && this.splashElement) {
      topHalf.innerHTML = this.splashElement.innerHTML;
      bottomHalf.innerHTML = this.splashElement.innerHTML;

      topHalf.style.cssText = this.splashElement.style.cssText;
      bottomHalf.style.cssText = this.splashElement.style.cssText;
    }

    [topHalf, bottomHalf].forEach((half) => {
      half.style.setProperty("display", "block", "important");
      half.style.setProperty("opacity", "1", "important");
      half.style.setProperty("visibility", "visible", "important");
    });

    topHalf.style.clipPath = SPLASH_CONFIG.TOP_HALF_CLIP;
    bottomHalf.style.clipPath = SPLASH_CONFIG.BOTTOM_HALF_CLIP;

    return { topHalf, bottomHalf };
  }

  show(): void {
    this.splashElement =
      document.getElementById("start-splash-enter") ||
      document.getElementById("page-splash-enter");

    if (!this.splashElement) {
      console.warn("Splash screen element not found");
      return;
    }

    const isStartSplash = this.splashElement.id === "start-splash-enter";
    const duration = isStartSplash
      ? SPLASH_CONFIG.START_SPLASH_DURATION
      : SPLASH_CONFIG.PAGE_SPLASH_DURATION;

    this.splashElement.classList.remove("hidden");
    this.preventScrollWithoutJump();

    if (isStartSplash) {
      // console.log("EXPLOD IT");
      explodeLogo();
    }

    setTimeout(() => {
      this.hide();
    }, duration);
  }

  showExit(callback?: Callback): void {
    const staticExitSplash = document.getElementById(
      "page-splash-exit"
    ) as HTMLElement | null;
    if (staticExitSplash) {
      staticExitSplash.style.setProperty("display", "none", "important");
      staticExitSplash.style.setProperty("opacity", "0", "important");
      staticExitSplash.style.setProperty("visibility", "hidden", "important");
    }

    this.preventScrollWithoutJump();

    const { topHalf, bottomHalf } = this.createAnimatedHalves({
      topId: "exit-splash-top-half",
      bottomId: "exit-splash-bottom-half",
      copyContent: false,
      baseClass: SPLASH_CONFIG.HALF_BASE_CLASS,
    });

    document.body.appendChild(topHalf);
    document.body.appendChild(bottomHalf);

    gsap.fromTo(
      [topHalf, bottomHalf],
      {
        y: (index: number, target: HTMLElement) =>
          target === topHalf
            ? SPLASH_CONFIG.OFF_SCREEN_TOP
            : SPLASH_CONFIG.OFF_SCREEN_BOTTOM,
      },
      {
        y: 0,
        duration: SPLASH_CONFIG.ANIMATION_DURATION,
        ease: SPLASH_CONFIG.EASING,
        onComplete: () => {
          if (callback) callback();
        },
      }
    );
  }

  navigateWithSplash(url: string): void {
    this.showExit(() => {
      window.location.href = url;
    });
  }

  preventScrollWithoutJump(): void {
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    this.originalOverflow = document.body.style.overflow;
    this.originalPaddingRight = document.body.style.paddingRight;

    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }

  restoreScrollWithoutJump(): void {
    document.body.style.overflow = this.originalOverflow || "";
    document.body.style.paddingRight = this.originalPaddingRight || "";
  }

  hide(): void {
    if (!this.splashElement || this.isAnimating) {
      return;
    }

    this.isAnimating = true;

    const allSplashElements = document.querySelectorAll<HTMLElement>(
      "#start-splash-enter, #page-splash-enter, #page-splash-exit, .splash-screen"
    );
    allSplashElements.forEach((el) => {
      el.style.setProperty("display", "none", "important");
    });

    const { topHalf, bottomHalf } = this.createAnimatedHalves({
      topId: "splash-top-half",
      bottomId: "splash-bottom-half",
      copyContent: true,
      baseClass: this.splashElement.className,
    });

    document.body.appendChild(topHalf);
    document.body.appendChild(bottomHalf);

    this.timeline = gsap.timeline({
      onComplete: () => {
        topHalf.remove();
        bottomHalf.remove();
        this.splashElement!.classList.add("hidden");

        this.restoreScrollWithoutJump();

        this.isAnimating = false;

        document.dispatchEvent(new CustomEvent("splashScreenComplete"));
      },
    });

    this.timeline
      .to(topHalf, {
        duration: SPLASH_CONFIG.ANIMATION_DURATION,
        ease: SPLASH_CONFIG.EASING,
        y: SPLASH_CONFIG.OFF_SCREEN_TOP,
      })
      .to(
        bottomHalf,
        {
          duration: SPLASH_CONFIG.ANIMATION_DURATION,
          ease: SPLASH_CONFIG.EASING,
          y: SPLASH_CONFIG.OFF_SCREEN_BOTTOM,
        },
        0
      );
  }

  forceHide(): void {
    if (this.timeline) {
      this.timeline.kill();
    }
    this.hide();
  }

  private isDebugNoSplash(): boolean {
    try {
      return (
        typeof window !== "undefined" &&
        window.location.hash.indexOf("no-splash") !== -1
      );
    } catch {
      return false;
    }
  }
}

// Auto-initialize when script loads
const splashScreen = new SplashScreen();

window.SplashScreen = SplashScreen;
window.splashScreenInstance = splashScreen;

window.showExitSplash = (callback?: Callback) => {
  splashScreen.showExit(callback);
};

window.navigateWithSplash = (url: string) => {
  splashScreen.showExit(() => {
    window.location.href = url;
  });
};

window.addEventListener("pageshow", (event: PageTransitionEvent) => {
  if (event.persisted) {
    splashScreen.cleanupExitSplash();
  }
});
